/**
 * Área SISTEMA: a aba de parâmetros do admin legado (só administrador), com as 11 sub-abas (`admin.html:1917` a `1927`).
 *
 * Cobre: usuários do painel e papéis, visibilidade de aba, vínculo produto e aba, e-mail de
 * referência por produto, preço padrão, manual de foto, roteiro de produtos, pessoas de indicação,
 * gift cards, cupons e "Cliente Comunidade". Esta última ENTRA por decisão do fundador de
 * 19/09/2026 (achado 9 do inventário): o nome cita a comunidade, mas ela só cria o login da cliente
 * pela Edge Function `criar-cliente-portal`, sem tocar tabela `comunidade_*`.
 *
 * Cada função reproduz UMA chamada do legado (`portal/admin.html`) e cita a linha. Os gift cards
 * também são lidos pelo Financeiro, com esta mesma função, e por isso a assinatura dela não muda.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum.
import {
  BUCKET_MATERIAIS,
  cabecalhoDeAutorizacao,
  clienteDe,
  executar,
  falha,
  invocarFuncao,
  sucesso,
  type FonteDeDados,
  type LinhaCuponsDesconto,
  type LinhaCuponsDescontoResgates,
  type LinhaGiftCards,
  type LinhaIndicadoresExternos,
  type LinhaParametroManualFoto,
  type LinhaParametrosProdutoAba,
  type LinhaParametrosProdutoEmail,
  type LinhaPrecosProdutos,
  type LinhaRoteiroProdutos,
  type LinhaUsuariosSistema,
  type LinhaVisibilidadeAbas,
  type Resultado,
} from '../nucleo.js';

/* ---------------------------------------------------------------------------------------------
 * USUÁRIOS DO PAINEL (`admin.html:8822` a `8927`)
 * ------------------------------------------------------------------------------------------- */

/** Todos os usuários do painel, pela ordem de cadastro. Legado: `admin.html:8825`, `.select('*').order('criado_em')`. */
export function listarUsuariosDoPainel(fonte: FonteDeDados): Promise<Resultado<LinhaUsuariosSistema[]>> {
  return executar<LinhaUsuariosSistema[]>(clienteDe(fonte).from('usuarios_sistema').select('*').order('criado_em'));
}

/** "Remover" um usuário: tira o acesso ao painel. Legado: `admin.html:8870`, `.delete().eq('email', email)`. */
export function removerUsuarioDoPainel(fonte: FonteDeDados, email: string): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('usuarios_sistema').delete().eq('email', email));
}

/** O que a Edge Function `criar-usuario-sistema` responde com status 2xx (lido em `admin.html:8903` a `8926`). */
export interface RespostaCriarUsuario {
  // `false` é recusa de regra de negócio, e não falha de sistema (`admin.html:8913`).
  readonly ok?: boolean;
  // O motivo da recusa ou do erro, em texto para a tela.
  readonly error?: string;
  // "conta_existente" pede o segundo passo, o de conceder sem trocar a senha.
  readonly codigo?: string;
  // Um aviso para mostrar no lugar de "Usuário adicionado.".
  readonly aviso?: string;
}

/** O que a tela manda para criar um usuário do painel. */
export interface PedidoCriarUsuario {
  readonly email: string;
  readonly senha: string;
  readonly papel: string;
  // Segundo passo, só depois de a pessoa confirmar (`admin.html:8910`).
  readonly conceder_sem_senha?: boolean;
}

/**
 * Cria o login e dá o papel, pela Edge Function, com o token da sessão no cabeçalho. Legado:
 * `admin.html:8896` a `8899`: é a ÚNICA chamada do legado que manda `Authorization` explícito.
 */
export async function criarUsuarioDoPainel(
  fonte: FonteDeDados,
  pedido: PedidoCriarUsuario,
): Promise<Resultado<RespostaCriarUsuario | null>> {
  // O cabeçalho sai do token da mesma sessão, lido do provedor pela fonte.
  const headers = await cabecalhoDeAutorizacao(fonte);
  return invocarFuncao<RespostaCriarUsuario | null>(fonte, 'criar-usuario-sistema', {
    headers,
    // O corpo com os mesmos nomes do legado; `conceder_sem_senha` só vai quando existe.
    body: { ...pedido },
  });
}

/* ---------------------------------------------------------------------------------------------
 * VISIBILIDADE DA ABA (`admin.html:8930` a `8994`)
 * ------------------------------------------------------------------------------------------- */

/** A visibilidade configurada de cada aba. Legado: `admin.html:8937`, `.select('*')`, sem ordem. */
export function listarVisibilidadeAbas(fonte: FonteDeDados): Promise<Resultado<LinhaVisibilidadeAbas[]>> {
  return executar<LinhaVisibilidadeAbas[]>(clienteDe(fonte).from('visibilidade_abas').select('*'));
}

/** "Salvar" de uma aba: grava ou substitui pela chave `aba`. Legado: `admin.html:8985`. */
export function salvarVisibilidadeAba(
  fonte: FonteDeDados,
  aba: string,
  visibilidade: string,
  atualizadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('visibilidade_abas')
      .upsert({ aba, visibilidade, atualizado_em: atualizadoEm }, { onConflict: 'aba' }),
  );
}

/* ---------------------------------------------------------------------------------------------
 * VÍNCULO PRODUTO E ABA (`admin.html:9020` a `9193`)
 * ------------------------------------------------------------------------------------------- */

/** Todos os vínculos, por produto e aba. Legado: `admin.html:9024`, `.select('*').order('produto').order('aba')`. */
export function listarVinculosProdutoAba(fonte: FonteDeDados): Promise<Resultado<LinhaParametrosProdutoAba[]>> {
  return executar<LinhaParametrosProdutoAba[]>(
    clienteDe(fonte).from('parametros_produto_aba').select('*').order('produto').order('aba'),
  );
}

/** "Adicionar" vínculo, devolvendo a linha criada. Legado: `admin.html:9183`. */
export function inserirVinculoProdutoAba(
  fonte: FonteDeDados,
  produto: string,
  aba: string,
  visibilidade: string,
): Promise<Resultado<LinhaParametrosProdutoAba>> {
  return executar<LinhaParametrosProdutoAba>(
    clienteDe(fonte).from('parametros_produto_aba').insert({ produto, aba, visibilidade }).select().single(),
  );
}

/** "Remover" vínculo. Legado: `admin.html:9091`, `.delete().eq('id', id)`. */
export function removerVinculoProdutoAba(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('parametros_produto_aba').delete().eq('id', id));
}

/**
 * Grava a ordem 0..n das abas de um produto, uma atualização por linha, todas ao mesmo tempo, e
 * devolve a primeira falha. Legado: `persistirOrdemAbas`, `admin.html:9157` a `9163`.
 */
export async function salvarOrdemDosVinculos(fonte: FonteDeDados, idsEmOrdem: readonly number[]): Promise<Resultado<null>> {
  const sb = clienteDe(fonte);
  // `Promise.all` como o legado: as gravações correm juntas, e nenhuma espera a outra.
  const resultados = await Promise.all(
    idsEmOrdem.map((id, indice) => executar<null>(sb.from('parametros_produto_aba').update({ ordem: indice }).eq('id', id))),
  );
  // A primeira falha, como o `resultados.find((r) => r.error)` do legado.
  const erro = resultados.find((r) => !r.ok);
  return erro ?? sucesso(null);
}

/* ---------------------------------------------------------------------------------------------
 * ARQUIVOS NO STORAGE, usados por "Vincular Produto E-mail" e por "Manual Foto"
 * ------------------------------------------------------------------------------------------- */

/**
 * Link assinado de uma hora para abrir um arquivo do bucket. Legado: `admin.html:9232` e `9396`,
 * `createSignedUrl(caminho, 3600)`: 3600 segundos, o prazo que o legado usa nos dois lugares.
 */
export async function linkAssinado(fonte: FonteDeDados, caminho: string): Promise<Resultado<string | null>> {
  const resultado = await executar<{ signedUrl: string } | null>(
    clienteDe(fonte).storage.from(BUCKET_MATERIAIS).createSignedUrl(caminho, 3600),
  );
  // A tela só precisa do endereço; sem endereço, `null`, e ela usa "#", como o legado.
  return resultado.ok ? sucesso(resultado.dados?.signedUrl ?? null) : resultado;
}

/** Apaga um arquivo do bucket. Legado: `admin.html:9261` e `9409` e `9436`, `remove([caminho])`. */
export function removerArquivoDoBucket(fonte: FonteDeDados, caminho: string): Promise<Resultado<unknown>> {
  return executar<unknown>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).remove([caminho]));
}

/**
 * Sobe um arquivo para o bucket. `substituir` liga o `upsert`, que o e-mail de produto usa
 * (`admin.html:9280`) e o manual de foto não usa (`admin.html:9431`).
 */
export function enviarArquivoAoBucket(
  fonte: FonteDeDados,
  caminho: string,
  arquivo: File,
  substituir: boolean,
): Promise<Resultado<unknown>> {
  const bucket = clienteDe(fonte).storage.from(BUCKET_MATERIAIS);
  // Só passa a opção quando o legado passa, para a chamada sem `upsert` ficar idêntica à do legado.
  return executar<unknown>(substituir ? bucket.upload(caminho, arquivo, { upsert: true }) : bucket.upload(caminho, arquivo));
}

/* ---------------------------------------------------------------------------------------------
 * VINCULAR PRODUTO E-MAIL (`admin.html:9205` a `9291`)
 * ------------------------------------------------------------------------------------------- */

/** Os e-mails de referência por produto. Legado: `admin.html:9208`, `.select('*').order('produto')`. */
export function listarEmailsDeProduto(fonte: FonteDeDados): Promise<Resultado<LinhaParametrosProdutoEmail[]>> {
  return executar<LinhaParametrosProdutoEmail[]>(clienteDe(fonte).from('parametros_produto_email').select('*').order('produto'));
}

/** Registra o arquivo vinculado ao produto. Legado: `admin.html:9283` a `9285`. */
export function inserirEmailDeProduto(
  fonte: FonteDeDados,
  produto: string,
  arquivoPath: string,
  arquivoNome: string,
): Promise<Resultado<LinhaParametrosProdutoEmail>> {
  return executar<LinhaParametrosProdutoEmail>(
    clienteDe(fonte)
      .from('parametros_produto_email')
      .insert({ produto, arquivo_path: arquivoPath, arquivo_nome: arquivoNome })
      .select()
      .single(),
  );
}

/** Apaga o vínculo do e-mail. Legado: `admin.html:9262`, `.delete().eq('id', id)`. */
export function removerEmailDeProduto(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('parametros_produto_email').delete().eq('id', id));
}

/* ---------------------------------------------------------------------------------------------
 * PREÇO PADRÃO DO PRODUTO (`admin.html:9319` a `9371`)
 * ------------------------------------------------------------------------------------------- */

/** Os preços cadastrados. Legado: `admin.html:9321`, `.select('*')`. */
export function listarPrecosProdutos(fonte: FonteDeDados): Promise<Resultado<LinhaPrecosProdutos[]>> {
  return executar<LinhaPrecosProdutos[]>(clienteDe(fonte).from('precos_produtos').select('*'));
}

/** "Salvar" de um preço: grava ou substitui pela chave `produto`. Legado: `admin.html:9366`. */
export function salvarPrecoProduto(
  fonte: FonteDeDados,
  produto: string,
  preco: number,
  atualizadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('precos_produtos')
      .upsert({ produto, preco, atualizado_em: atualizadoEm }, { onConflict: 'produto' }),
  );
}

/* ---------------------------------------------------------------------------------------------
 * MANUAL FOTO (`admin.html:9383` a `9457`)
 * ------------------------------------------------------------------------------------------- */

/**
 * O arquivo atual do manual. Legado: `admin.html:9386`, `.select('*').maybeSingle()`: a tabela pode
 * estar vazia, e aí volta `null`, que a tela mostra como "Nenhum arquivo enviado ainda.".
 */
export function lerManualFoto(fonte: FonteDeDados): Promise<Resultado<LinhaParametroManualFoto | null>> {
  return executar<LinhaParametroManualFoto | null>(clienteDe(fonte).from('parametro_manual_foto').select('*').maybeSingle());
}

/** "Excluir" o manual: apaga a linha. Legado: `admin.html:9410`. */
export function apagarManualFoto(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('parametro_manual_foto').delete().eq('id', id));
}

/** Substitui o arquivo na MESMA linha, com a hora. Legado: `admin.html:9438` a `9441`. */
export function substituirManualFoto(
  fonte: FonteDeDados,
  id: number,
  arquivoPath: string,
  arquivoNome: string,
  atualizadoEm: string,
): Promise<Resultado<LinhaParametroManualFoto>> {
  return executar<LinhaParametroManualFoto>(
    clienteDe(fonte)
      .from('parametro_manual_foto')
      .update({ arquivo_path: arquivoPath, arquivo_nome: arquivoNome, atualizado_em: atualizadoEm })
      .eq('id', id)
      .select()
      .single(),
  );
}

/** Primeiro arquivo do manual: insere a linha. Legado: `admin.html:9445` a `9447`. */
export function inserirManualFoto(
  fonte: FonteDeDados,
  arquivoPath: string,
  arquivoNome: string,
): Promise<Resultado<LinhaParametroManualFoto>> {
  return executar<LinhaParametroManualFoto>(
    clienteDe(fonte).from('parametro_manual_foto').insert({ arquivo_path: arquivoPath, arquivo_nome: arquivoNome }).select().single(),
  );
}

/* ---------------------------------------------------------------------------------------------
 * ROTEIRO CLIENTE (`admin.html:9465` a `9519`)
 * ------------------------------------------------------------------------------------------- */

/** A sequência dos produtos. Legado: `admin.html:9468`, `.select('*').order('ordem')`. */
export function listarRoteiroProdutos(fonte: FonteDeDados): Promise<Resultado<LinhaRoteiroProdutos[]>> {
  return executar<LinhaRoteiroProdutos[]>(clienteDe(fonte).from('roteiro_produtos').select('*').order('ordem'));
}

/**
 * Grava a nova ordem 0..n, uma atualização por produto, todas juntas. Legado:
 * `persistirOrdemRoteiroProdutos`, `admin.html:9503` a `9506`.
 */
export async function salvarOrdemDoRoteiro(
  fonte: FonteDeDados,
  produtosEmOrdem: readonly string[],
  atualizadoEm: string,
): Promise<Resultado<null>> {
  const sb = clienteDe(fonte);
  const resultados = await Promise.all(
    produtosEmOrdem.map((produto, indice) =>
      executar<null>(sb.from('roteiro_produtos').update({ ordem: indice, atualizado_em: atualizadoEm }).eq('produto', produto)),
    ),
  );
  const erro = resultados.find((r) => !r.ok);
  return erro ?? sucesso(null);
}

/**
 * Cria a sequência inicial do roteiro, por decisão do fundador em 07/10/2026.
 *
 * POR QUE ELA NASCEU AQUI, e não existia antes: `roteiro_produtos` não tem seed em migration
 * nenhuma (a de `20260812_040_financeiro_agenda_parametros.sql:104` só cria a tabela), e o legado
 * nunca inseriu linha: ele só atualizava a `ordem` de quem já estava lá (`admin.html:9503`). Com a
 * tabela vazia, o admin mostrava um aviso de desenvolvedor e não havia caminho na tela para sair
 * dali. Agora a consultora resolve sozinha, em qualquer ambiente, sem credencial de banco.
 *
 * É UPSERT, e não insert, porque a chave primária é o próprio `produto`: se alguma linha já
 * existir, a semeadura reescreve a ordem dela em vez de estourar com chave duplicada. E vai numa
 * chamada só, porque o que está sendo criado é a sequência inteira, e não oito linhas soltas.
 */
export function semearRoteiroProdutos(
  fonte: FonteDeDados,
  produtosEmOrdem: readonly string[],
  atualizadoEm: string,
): Promise<Resultado<LinhaRoteiroProdutos[]>> {
  return executar<LinhaRoteiroProdutos[]>(
    clienteDe(fonte)
      .from('roteiro_produtos')
      // A ordem começa em 1 por pedido do fundador, e a tela só lê a ordem relativa
      // (`(a.ordem ?? 999) - (b.ordem ?? 999)`), então o primeiro arraste a regrava em 0..n.
      .upsert(
        produtosEmOrdem.map((produto, indice) => ({ produto, ordem: indice + 1, atualizado_em: atualizadoEm })),
        { onConflict: 'produto' },
      )
      .select(),
  );
}

/* ---------------------------------------------------------------------------------------------
 * PESSOAS INDICAÇÃO (`admin.html:7040` a `7122`)
 * ------------------------------------------------------------------------------------------- */

/** As pessoas de fora que indicam, por nome. Legado: `admin.html:7041`, `.select('*').order('nome')`. */
export function listarPessoasIndicacao(fonte: FonteDeDados): Promise<Resultado<LinhaIndicadoresExternos[]>> {
  return executar<LinhaIndicadoresExternos[]>(clienteDe(fonte).from('indicadores_externos').select('*').order('nome'));
}

/**
 * "Salvar" o nome. Legado: `admin.html:7090`.
 *
 * O `id` chega como a tela o manda. A coluna é `uuid` (`supabase/migrations/20260812_010_base.sql:25`)
 * e o legado passa `parseInt(id, 10)` (`admin.html:7087`), que transforma o uuid em `NaN` ou num
 * número solto. Por decisão do fundador esse defeito é REPRODUZIDO, e a reprodução mora num ponto só,
 * na tela (`apps/admin/src/telas/sistema/pessoas-indicacao.tsx`, `REPRODUZIR_PARSEINT_DO_ID`): o
 * filtro que sai daqui é o mesmo do legado (`id=eq.3` ou `id=eq.NaN`).
 */
export function renomearPessoaIndicacao(fonte: FonteDeDados, id: string, nome: string): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('indicadores_externos').update({ nome }).eq('id', id));
}

/** "Excluir" a pessoa. Legado: `admin.html:7102`, com o mesmo `id` da tela explicado acima (`admin.html:7101`). */
export function excluirPessoaIndicacao(fonte: FonteDeDados, id: string): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('indicadores_externos').delete().eq('id', id));
}

/** "Adicionar" pessoa, devolvendo a linha criada. Legado: `admin.html:7114`. */
export function inserirPessoaIndicacao(fonte: FonteDeDados, nome: string): Promise<Resultado<LinhaIndicadoresExternos>> {
  return executar<LinhaIndicadoresExternos>(clienteDe(fonte).from('indicadores_externos').insert({ nome }).select().single());
}

/* ---------------------------------------------------------------------------------------------
 * GIFT CARDS (`admin.html:7130` a `7283`)
 * ------------------------------------------------------------------------------------------- */

/** Todos os gift cards, do mais novo para o mais antigo. Legado: `admin.html:7149`, `.order('emitido_em', desc)`. */
export function listarGiftCards(fonte: FonteDeDados): Promise<Resultado<LinhaGiftCards[]>> {
  return executar<LinhaGiftCards[]>(clienteDe(fonte).from('gift_cards').select('*').order('emitido_em', { ascending: false }));
}

/** Os campos de um gift card novo, com os nomes do legado. */
export interface GiftCardNovo {
  readonly codigo: string;
  readonly valor: number;
  readonly nome_comprador: string;
  readonly nome_destinatario: string;
  readonly validade: string | null;
  readonly email_envio: string;
}

/** Uma tentativa de emissão, devolvendo a linha criada. Legado: `admin.html:7255` a `7258`. */
export function inserirGiftCard(fonte: FonteDeDados, giftCard: GiftCardNovo): Promise<Resultado<LinhaGiftCards>> {
  return executar<LinhaGiftCards>(clienteDe(fonte).from('gift_cards').insert({ ...giftCard }).select().single());
}

/**
 * Dispara o e-mail "Você ganhou um Gift Card!". Legado: `admin.html:7265` a `7267`, SEM esperar a
 * resposta: a tela chama e segue, e a falha só vai ao console, como no legado.
 */
export function notificarGiftCardEmitido(
  fonte: FonteDeDados,
  dados: { email_envio: string; nome_destinatario: string; codigo: string; valor: number; validade: string | null },
): Promise<Resultado<unknown>> {
  return invocarFuncao<unknown>(fonte, 'notificar-giftcard-emitido', { body: { ...dados } });
}

/** "Excluir" um gift card não resgatado. Legado: `admin.html:7224`. */
export function excluirGiftCard(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('gift_cards').delete().eq('id', id));
}

/* ---------------------------------------------------------------------------------------------
 * CUPONS DE DESCONTO (`admin.html:7285` a `7436`)
 * ------------------------------------------------------------------------------------------- */

/** Os cupons e todos os resgates, lidos juntos. */
export interface CuponsEResgates {
  readonly cupons: LinhaCuponsDesconto[];
  readonly resgates: LinhaCuponsDescontoResgates[];
}

/**
 * Cupons do mais novo para o mais antigo, e todos os resgates, ao mesmo tempo. Legado:
 * `carregarCuponsDados`, `admin.html:7322` a `7327`. Só a falha dos CUPONS derruba a leitura; a
 * dos resgates vira lista vazia, como o `resgates.data || []` do legado.
 */
export async function listarCuponsEResgates(fonte: FonteDeDados): Promise<Resultado<CuponsEResgates>> {
  const sb = clienteDe(fonte);
  const [cupons, resgates] = await Promise.all([
    executar<LinhaCuponsDesconto[]>(sb.from('cupons_desconto').select('*').order('criado_em', { ascending: false })),
    executar<LinhaCuponsDescontoResgates[]>(sb.from('cupons_desconto_resgates').select('*')),
  ]);
  if (!cupons.ok) return falha(cupons.erro.mensagem, { codigo: cupons.erro.codigo, status: cupons.erro.status });
  return sucesso({ cupons: cupons.dados ?? [], resgates: resgates.ok ? (resgates.dados ?? []) : [] });
}

/** "Desativar" ou "Ativar": grava o valor novo. Legado: `admin.html:7390`. */
export function definirCupomAtivo(fonte: FonteDeDados, id: number, ativo: boolean): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('cupons_desconto').update({ ativo }).eq('id', id));
}

/** "Excluir" o cupom; os resgates saem em cascata no banco. Legado: `admin.html:7401`. */
export function excluirCupom(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('cupons_desconto').delete().eq('id', id));
}

/** "Criar" cupom, devolvendo a linha criada. Legado: `admin.html:7422` a `7424`. */
export function criarCupom(
  fonte: FonteDeDados,
  cupom: { codigo: string; tipo_desconto: string; valor_desconto: number; validade: string | null },
): Promise<Resultado<LinhaCuponsDesconto>> {
  return executar<LinhaCuponsDesconto>(clienteDe(fonte).from('cupons_desconto').insert({ ...cupom }).select().single());
}

/* ---------------------------------------------------------------------------------------------
 * CLIENTE COMUNIDADE (`admin.html:7442` a `7468`)
 * ------------------------------------------------------------------------------------------- */

/** O que a Edge Function `criar-cliente-portal` responde com status 2xx (lido em `admin.html:7457` a `7462`). */
export interface RespostaCriarClienteDoPortal {
  // O motivo da recusa, que a tela mostra depois de "Erro: ".
  readonly error?: string;
  // Um aviso para mostrar como "Acesso criado, mas: " seguido dele.
  readonly aviso?: string;
}

/**
 * "Criar acesso" da sub-aba "Cliente Comunidade": cria o login da cliente do portal e manda o
 * e-mail com a senha provisória. Legado: `admin.html:7455`,
 * `sb.functions.invoke('criar-cliente-portal', { body: { nome, whatsapp, email } })`, com o token
 * da sessão que o próprio cliente põe (sem `Authorization` explícito, ao contrário de `8897`).
 */
export function criarAcessoClienteDoPortal(
  fonte: FonteDeDados,
  pedido: { readonly nome: string; readonly whatsapp: string; readonly email: string },
): Promise<Resultado<RespostaCriarClienteDoPortal | null>> {
  // O corpo com os três nomes do legado, e nada mais: a função não espera outro campo.
  return invocarFuncao<RespostaCriarClienteDoPortal | null>(fonte, 'criar-cliente-portal', { body: { ...pedido } });
}
